/** Weergavelogica voor de DSO-subkoppen, los van React zodat hij te testen is. */

type Result = Record<string, unknown>;

/** Velden die iets over de analyse zeggen, niet over de regel zelf. */
const META = new Set(["vanToepassing", "bronWId", "eenheid"]);

const isEmpty = (v: unknown) =>
  v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

const formatValue = (v: unknown): string => {
  if (Array.isArray(v)) return v.map(formatValue).filter(Boolean).join(", ");
  if (v && typeof v === "object")
    return Object.entries(v)
      .filter(([, w]) => !isEmpty(w))
      .map(([k, w]) => `${k} ${formatValue(w)}`)
      .join(", ");
  return isEmpty(v) ? "" : String(v);
};

const generic = (result: Result) =>
  Object.entries(result)
    .filter(([k, v]) => !META.has(k) && !isEmpty(v))
    .map(([k, v]) => `${k}: ${formatValue(v)}`)
    .join("; ") || null;

// Sleutels zijn de analyse-slugs van de monitor, velden zijn zijn resultaat-JSON
const KNOWN: Record<string, (r: Result) => string | null> = {
  "max-bouwhoogte": ({ maxBouwhoogte, eenheid, voorwaarde }) => {
    const height = isEmpty(maxBouwhoogte) ? "" : `${maxBouwhoogte} ${eenheid || "m"}`;
    if (!height) return isEmpty(voorwaarde) ? null : String(voorwaarde);
    return isEmpty(voorwaarde) ? height : `${height} (${voorwaarde})`;
  },
  geluidnormen: ({ normen }) =>
    Array.isArray(normen) && normen.length
      ? normen.map((n: { waardeDb: number }) => `${n.waardeDb} dB`).join(", ")
      : null,
};

/**
 * Een geëxtraheerde regel als leesbare tekst. Bekende soorten netjes ("12 m",
 * "50 dB"); een soort die de monitor later toevoegt verschijnt vanzelf als
 * "sleutel: waarde" — ruw, maar nooit stuk. Niets te melden → `null`.
 */
export const analysisText = (analysis: string, result: Result): string | null =>
  // Mist een bekende soort zijn eigen velden, dan zijn die leeg en toont de
  // generieke vorm wat er wél is
  KNOWN[analysis]?.(result) || generic(result);

/**
 * "art. 22.66 lid 1" uit de wId-staart van het DSO (`["art_22.66", "para_1"]`)
 * of uit een kaal IMRO-artikelnummer (`"10"`).
 */
export const articleLabel = (article: string | string[]): string => {
  if (!Array.isArray(article)) return article ? `art. ${article}` : "";
  return article
    .map((part) =>
      part
        .replace(/^art_/, "art. ")
        .replace(/^para_/, "lid ")
        .replace(/_/g, " ")
    )
    .join(" ");
};

/** "gemeente - 12 artikelen van toepassing" onder de titel van een regeling. */
export const regulationLine = (r: {
  bestuurslaag: string;
  soort: string | null;
  artikelen: number;
}) =>
  [
    r.bestuurslaag || r.soort,
    r.artikelen
      ? `${r.artikelen} ${r.artikelen === 1 ? "artikel" : "artikelen"} van toepassing`
      : "",
  ]
    .filter(Boolean)
    .join(" - ");

const LEVELS = ["gemeente", "waterschap", "provincie", "rijk"];
// Wat bindt eerst; visie en programma's sturen alleen het bestuur zelf
const KINDS = [/plan|verordening|amvb|regeling/i, /visie/i, /programma/i];

type Sortable = { bestuurslaag: string; soort: string | null };

const lastIfUnknown = (i: number, length: number) => (i < 0 ? length : i);
const levelRank = (r: Sortable) => lastIfUnknown(LEVELS.indexOf(r.bestuurslaag), LEVELS.length);
const kindRank = (r: Sortable) =>
  lastIfUnknown(
    KINDS.findIndex((k) => k.test(r.soort ?? "")),
    KINDS.length
  );

/**
 * Het dichtstbije bestuur eerst, en daarbinnen wat bindt vóór visie en
 * programma's: anders staat het omgevingsplan achter vier provinciale
 * programma's. Alleen voor de weergave — de chatprompt houdt de volgorde van
 * de monitor.
 */
export const sortRegulations = <T extends Sortable>(regulations: T[]) =>
  [...regulations].sort((a, b) => levelRank(a) - levelRank(b) || kindRank(a) - kindRank(b));

/**
 * De kaartlink naast de titel, alleen als hij iets toevoegt. Bij een regeling
 * leest `url` de tekst en toont `kaartUrl` Regels op de kaart; bij een IMRO-plan
 * vallen ze samen (er is geen resolver), en dan is één link genoeg.
 * Beide komen kant-en-klaar van de monitor — wij bouwen er geen na.
 */
export const mapLink = (item: { url?: string | null; kaartUrl?: string | null }) =>
  item.kaartUrl && item.kaartUrl !== item.url ? item.kaartUrl : undefined;
